import React, { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Rating,
  IconButton,
  Stack,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MailIcon from "@mui/icons-material/Mail";
import LanguageIcon from "@mui/icons-material/Language";
import CallIcon from "@mui/icons-material/Call";
import StarIcon from "@mui/icons-material/Star";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useNavigate } from "react-router-dom";
import { ASSET_BASE } from "../../utils/baseUrls";

export default function MaterialProductCard({
  name,
  location,
  email,
  website,
  phone,
  gst,
  established,
  id,
  images,
  rating,
  reviewCount,
  userid,
  materialid,
  description,
  materialCode,
  selectionMode = false,
  viewType = "grid",          // ← CHANGED: grid is now default
  onSelect,
  onShopView,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const getImageUrl = (img) => {
    if (!img) return "/assets/images/placeholder_img.png";
    if (typeof img !== "string" && Array.isArray(img)) img = img[0];
    if (typeof img !== "string") return "/assets/images/placeholder_img.png";
    if (img.startsWith("http")) return img;
    return `${ASSET_BASE}storage/${img}`;
  };

  const imageUrl = getImageUrl(images);

  const maskPhone = (phoneStr) => {
    if (!phoneStr) return "";
    const str = String(phoneStr).trim();
    if (str.length <= 4) return str;
    const first2 = str.slice(0, 2);
    const last2 = str.slice(-2);
    const middleMask = "X".repeat(str.length - 4);
    return `${first2}${middleMask}${last2}`;
  };

  const maskEmail = (emailStr) => {
    if (!emailStr) return "";
    const str = String(emailStr).trim();
    const atIndex = str.indexOf("@");
    if (atIndex === -1) {
      if (str.length <= 4) return str;
      return str.slice(0, 2) + "X".repeat(str.length - 4) + str.slice(-2);
    }
    const localPart = str.slice(0, atIndex);
    const domainPart = str.slice(atIndex);
    if (localPart.length <= 4) {
      return localPart + domainPart;
    }
    const first2 = localPart.slice(0, 2);
    const last2 = localPart.slice(-2);
    const middleMask = "X".repeat(localPart.length - 4);
    return `${first2}${middleMask}${last2}${domainPart}`;
  };

  // Selection mode (simplified card - same for both views)
  if (selectionMode) {
    return (
      <Card
        sx={{
          width: "100%",
          background: "#FBFAF7",
          borderRadius: 3,
          boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
          cursor: "pointer",
          transition: "transform 0.2s",
          "&:hover": { transform: "translateY(-4px)" },
          textAlign: "center",
          p: 2,
        }}
        onClick={() => onSelect && onSelect(id, name)}
      >
        <Box
          sx={{
            width: "100%",
            height: 150,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #95B160", // Consistent signature border
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            mb: 1.5,
          }}
        >
          <Box
            component="img"
            src={imageUrl || "/assets/images/placeholder_img.png"}
            alt={name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: imageUrl ? "cover" : "contain",
              p: images ? 0 : 2,
            }}
          />
        </Box>
        <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ color: "#000" }}>
          {name || "Material"}
        </Typography>
        {materialCode && (
          <Typography fontSize={12} sx={{ color: "#666", mt: 0.3 }}>
            ASN CODE - {materialCode}
          </Typography>
        )}
      </Card>
    );
  }

  // Normal mode - Grid is now default
  const isGrid = viewType === "grid";

  return (
    <Card
      sx={{
        width: "100%",
        background: "#FBFAF7",
        borderRadius: isGrid ? 4 : 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        overflow: "hidden",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isGrid ? "column" : { xs: "column", sm: "row" },
            gap: isGrid ? 2 : 4,
          }}
        >
          {/* IMAGE */}
          <Box
            sx={{
              width: isGrid ? "100%" : { xs: "100%", sm: 220 },
              height: isGrid ? 200 : { xs: 200, sm: 220 },
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #95B160",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: isGrid ? "#FBFAF7" : "#fff",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={name || "shop"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                p: images ? 1.5 : 3,
              }}
            />
          </Box>

          {/* CONTENT */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: isGrid ? 0.8 : 1 }}>
            {/* Name */}
            <Typography
              variant={isGrid ? "subtitle1" : "h5"}
              fontWeight={700}
              sx={{
                textAlign: "left",
                lineHeight: 1.3,
                width: "100%",
                wordBreak: "break-word",
                display: isGrid ? "-webkit-box" : "block",
                WebkitLineClamp: isGrid ? 2 : "none",
                WebkitBoxOrient: "vertical",
                overflow: isGrid ? "hidden" : "visible",
                minHeight: isGrid ? "2.6em" : "auto"
              }}
            >
              {name || "Material Store"}
            </Typography>

            {/* Location (Pin + Text) */}
            {/* <Box display="flex" alignItems="flex-start" gap={0.8} sx={{ minHeight: "2.8em" }}>
              <LocationOnIcon fontSize="small" sx={{ color: "#82A240", mt: 0.3 }} />
              <Typography 
                fontSize={14} 
                color="#666"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                  width: "100%",
                  wordBreak: "break-word"
                }}
              >
                {location?.includes(",") 
                  ? location.split(",").slice(1).join(",").trim() 
                  : (location || "Chennai, Tamil Nadu, India")}
              </Typography>
            </Box> */}

            {/* Phone (Icon + Green Underlined) - ALWAYS SHOW IN GRID Too */}
            {phone && (
              <Box display="flex" alignItems="center" gap={0.8}>
                <CallIcon fontSize="small" sx={{ color: "#82A240" }} />
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  sx={{
                    color: "#82A240",
                    textDecoration: "underline",
                    cursor: "pointer"
                  }}
                >
                  {maskPhone(phone)}
                </Typography>
              </Box>
            )}

            {/* Rating */}
            <Box display="flex" alignItems="center" gap={1}>
              <Rating
                value={rating || 0}
                precision={0.5}
                readOnly
                size="small"
                icon={<StarIcon fontSize="inherit" sx={{ color: "#FEB821" }} />}
              />
              <Typography fontSize={14} color="#666">
                {rating || "0.0"} ({reviewCount || 0} Reviews)
              </Typography>
            </Box>

            {!isGrid && (
              /* Social icons row (specifically for list view) */
              <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 4, mt: 1, mb: 1.5 }}>
                {/* Email & Website badges used in List View */}
                <InfoItem icon={<MailIcon sx={{ fontSize: 18 }} />} text={maskEmail(email || "nilatrades@gmail.com")} />
                <InfoItem icon={<LanguageIcon sx={{ fontSize: 18 }} />} text={website || "https://www.website.com"} />
                {/* <IconButton size="small" sx={{ ml: "auto", bgcolor: "#FBAF24", color: "#fff", "&:hover": { bgcolor: "#e09a1f" }, p: 0.5 }}>
                  <InstagramIcon sx={{ fontSize: 18 }} />
                </IconButton> */}
              </Box>
            )}

            {/* GSTIN & Established */}
            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                flexDirection: isGrid ? "column" : "row",
                gap: isGrid ? "0.2rem" : "5.2rem"
              }}
            >
              <Typography fontSize={13} color="#666">
                GSTIN: {gst || "29ABCDE1234F1Z5"}
              </Typography>
              <Typography fontSize={13} color="#666">
                Established Year: {established || "1998"}
              </Typography>
            </Box>

            {/* View Button */}
            <Button
              variant="contained"
              fullWidth={isGrid}
              sx={{
                mt: 1.5,
                background: "#82A240",
                color: "#fff",
                textTransform: "none",
                px: isGrid ? 2 : 3,
                borderRadius: "10px",
                fontWeight: 600,
                "&:hover": { background: "#6A8A35" },
                minWidth: isGrid ? "100%" : "130px",
                py: 0.8,
                alignSelf: isGrid ? "stretch" : "flex-end"
              }}
              onClick={(e) => {
                e.stopPropagation();
                onShopView?.();
              }}
            >
              View Shop
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/* Helper Components */
const InfoItem = ({ icon, text }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box
      sx={{
        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        borderRadius: "50%",
        p: 0.4,
        backgroundColor: "#FFFFFF",
        color: "#95B160",
      }}
    >
      {icon}
    </Box>
    <Typography fontSize={14} sx={{ color: "#6BAF4B", wordBreak: "break-word" }}>
      {text}
    </Typography>
  </Box>
);